import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getMessageForFetchError } from "../shared/utils/apiError";
import ErrorBanner from "../components/ErrorBanner";
import EventDashboardMetricsFilters from "../components/EventDashboardMetricsFilters";
import { NoShowWalkInTrendCard } from "./RegistrationMetrics";
import { buildEventsManagementPath, EVENT_MGMT_TAB_PARTICIPANTS } from "../shared/utils/eventManagementTabs";
import { filterEventsByManagementFilters } from "../shared/utils/eventManagementFilters";
import { useEventProgramHostedByFilters } from "../shared/hooks/useEventProgramHostedByFilters";
import { useGlobalTimeNavigator } from "../shared/hooks/useGlobalTimeNavigator";


import { apiFetch } from "../shared/api/apiFetch";

const uiConfig = require("../uiConfig");

function ClosedEventsMetrics() {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const account_id = userInfo?.account_id;
  const serverUrl = uiConfig.serverInfo.serverUrl;
  const {
    programs,
    filterProgramId,
    setFilterProgramId,
    filterOrganizerId,
    setFilterOrganizerId,
    filterProgramMembers,
  } = useEventProgramHostedByFilters({ account_id });
  const timeNav = useGlobalTimeNavigator({ initialView: "all" });

  function handleDrillToEventParticipants(attendanceType) {
    // From Closed Events Metrics, drill to Event Participants in table view.
    // All Time keeps last-12-months scope; a bounded time filter uses the navigator range instead.
    navigate(buildEventsManagementPath({
      tab: EVENT_MGMT_TAB_PARTICIPANTS,
      view: "table",
      status: "closed",
      ...(timeNav.view === "all" ? { scope: "closed_metrics" } : {}),
      ...(filterProgramId ? { program: filterProgramId } : {}),
      navView: timeNav.view,
      navDate: timeNav.focusDateYmd,
      ...(attendanceType ? { attendance: attendanceType } : {}),
    }));
  }

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [bannerError, setBannerError] = useState("");

  const loadEvents = useCallback(() => {
    if (!account_id) {
      setLoading(false);
      return;
    }
    setBannerError("");
    apiFetch(serverUrl + "/getEventsWithDetails", {
      method: "POST",
      body: JSON.stringify({ account_id }),
    })
      .then((resp) => resp.json())
      .then((data) => setEvents(Array.isArray(data) ? data : []))
      .catch((err) => setBannerError(getMessageForFetchError(err)))
      .finally(() => setLoading(false));
  }, [account_id, serverUrl]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const filteredEvents = useMemo(
    () =>
      filterEventsByManagementFilters(events, {
        programId: filterProgramId,
        organizerId: filterOrganizerId,
        timeNav,
      }),
    [events, filterProgramId, filterOrganizerId, timeNav]
  );

  const metricsFilterBar = (
    <EventDashboardMetricsFilters
      programs={programs}
      filterProgramId={filterProgramId}
      onProgramChange={setFilterProgramId}
      filterOrganizerId={filterOrganizerId}
      onOrganizerChange={setFilterOrganizerId}
      filterProgramMembers={filterProgramMembers}
      timeNav={timeNav}
    />
  );

  if (loading) {
    return (
      <div style={{ padding: "24px" }}>
        <h2 style={{ margin: 0, fontSize: "1.25rem", fontWeight: 600 }}>Closed Events Metrics</h2>
        {metricsFilterBar}
        <p style={{ marginTop: "12px", color: "#6c757d" }}>Loading…</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, minWidth: 0, overflowX: "hidden", overflowY: "auto" }}>
      <div style={{ flexShrink: 0, padding: "2px 0 4px 0", borderBottom: "1px solid #dee2e6", marginBottom: "6px" }}>
        <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 600, color: "#212529" }}>Closed Events Metrics</h2>
        <p style={{ margin: "2px 0 0", fontSize: "11px", color: "#6c757d" }}>
          No-show, walk-in, waitlisted and cancelled trends for closed events. Overall and monthly view for the last 12 months. Click a bar to open Event Participants in table view with matching attendance filter so counts match.
        </p>
      </div>
      {metricsFilterBar}
      <ErrorBanner message={bannerError} onDismiss={() => setBannerError("")} />

      <div style={{ flex: 1, minHeight: 0, overflow: "auto", padding: "0" }}>
        <NoShowWalkInTrendCard
          events={filteredEvents}
          last12MonthsOnly={timeNav.view === "all"}
          onDrillToEventParticipants={handleDrillToEventParticipants}
        />
      </div>
    </div>
  );
}

export default ClosedEventsMetrics;
